import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useFormik } from 'formik';
import * as Yup from 'yup';

// Components
import CustomButton from '../../components/CustomButton';
import Label from '../../components/Label';
import { TextAreaWithCounterLimit } from '../TextAreaWithCounterLimit';

// Prime-React-Components
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { classNames } from 'primereact/utils';
// import { AutoComplete } from 'primereact/autocomplete';
import { confirmDialog } from 'primereact/confirmdialog';
import { InputNumber } from 'primereact/inputnumber';

// Constants
import {
  BUTTON_TYPES,
  FEEDBACK_TEXTAREA_LIMIT,
  HR_OVERALL_FEEDBACK,
  LIMITED_HR_FEEDBACK,
  HR_OVERALL_FEEDBACK_STATUS,
  INITIAL_PAGE_NUMBER,
  INITIAL_SIZE,
  InterviewType,
  Interview_Category,
  LABEL_TYPES,
  MSG_TYPES,
  OVERALL_GRADE,
  ROUTES,
  TEXTAREA_LIMIT
} from '../../constants/common';

// Redux-Actions
import { addFeedback, getInterviewHistoryList } from '../../redux/actions/interviews';
import { getJobDesignationBySearch } from '../../redux/actions/hr';

// Redux-Slices
import { apiStatusClear } from '../../redux/slices/apiStatus';
import { useNavigate } from 'react-router-dom';

// Utils
import { wordCapitalize } from '../../utils/common';
import { MultiSelect } from 'primereact/multiselect';
import { STATUS, STATUS_INTERVIEW } from '../../constants/common';

const AddFeedback = (props) => {
  // Props.
  const { id, interviewType, confirmDialogMessage, disabled, feedbackData, interviewCategory } =
    props;

  // Redux hooks.
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { userDetails } = useSelector((state) => state);
  const { isLoading } = useSelector((state) => state.apiStatus);
  const { searchData } = useSelector((state) => state.hrs);
  const currentRound = userDetails?.RoundOfInterview?.find((round) => round.ID === id);
  const isHRRound = props.interviewType === InterviewType['HR Round'];

  // React useState hooks.
  const [designationOptions, setDesignationOptions] = useState([]);
  // const [designationText, setDesignationText] = useState('');

  // React useEffect hooks.
  useEffect(() => {
    if (searchData) {
      const modifiedDesignationOptions =
        searchData && searchData.length > 0 ? searchData : [{ Designation: MSG_TYPES.NO_RESULT }];
      setDesignationOptions(modifiedDesignationOptions);
    }
  }, [searchData]);

  useEffect(() => {
    dispatch(getJobDesignationBySearch(''));
  }, []);

  let validationShape;
  const commonValidationShape = {
    overallGrade: Yup.string().required('Please select an overall grade'),
    technicalAttitude: Yup.string().required('Please select an attitude grade'),
    technicalCommunication: Yup.string().required('Please select communication grade'),
    result: Yup.string().required('Please enter result'),
    feedbackNote: Yup.string()
      .trim()
      .min(
        interviewCategory === Interview_Category['Non Technical'] ? 4 : TEXTAREA_LIMIT.MIN,
        'Feedback note should be at least ${min} characters.'
      )
      .max(TEXTAREA_LIMIT.MAX, 'Feedback note should not exceed ${max} characters.')
      .nullable()
      .required('Please enter notes.')
  };

  if (
    props.interviewType === InterviewType['Technical Interview - 1'] ||
    props.interviewType === InterviewType['Technical Interview - 2'] ||
    props.interviewType === InterviewType['Management Round']
  ) {
    validationShape = Yup.object().shape({
      ...commonValidationShape
      // overallFeedback: Yup.string().required('Please select feedback')
    });
  } else if (props.interviewType === InterviewType.Practical) {
    validationShape = Yup.object().shape({
      ...commonValidationShape,
      codeQuality: Yup.string().required('Please enter code quality')
    });
  } else if (props.interviewType === InterviewType['HR Round']) {
    validationShape = Yup.object().shape({
      ...commonValidationShape,
      // overallFeedback: Yup.string().required('Please select feedback'),
      jobDesignation: Yup.string().when('result', {
        is: HR_OVERALL_FEEDBACK_STATUS['Not Selected'],
        then: Yup.string(),
        otherwise: Yup.string().required('Please select a designation')
      }),
      package: Yup.number().when('result', {
        is: HR_OVERALL_FEEDBACK_STATUS['Not Selected'],
        then: Yup.number('package must be of type number.')
          .min(0, 'package should not be less then 0.')
          .max(99, 'package should not be more then 99.')
          .typeError('Only positive numbers are allowed.'),
        otherwise: Yup.number('package must be of type number.')
          .min(0, 'package should not be less then 0.')
          .max(99, 'package should not be more then 99.')
          .typeError('Only positive numbers are allowed.')
          .required('Please enter package value')
      }),
      yearsOfExperience: Yup.number().when('result', {
        is: HR_OVERALL_FEEDBACK_STATUS['Not Selected'],
        then: Yup.number(),
        otherwise: Yup.number()
          .min(0, 'Minimum 0 Year.')
          .max(100, 'Maximum 100 Years.')
          .typeError('Only numbers are allowed.')
          .required('Exp. in Years is Required.')
      }),
      monthsOfExperience: Yup.number().when('result', {
        is: HR_OVERALL_FEEDBACK_STATUS['Not Selected'],
        then: Yup.number(),
        otherwise: Yup.number()
          .min(0, 'Minimum 0 Month.')
          .max(11, 'Maximum 11 Months.')
          .required('Exp. in Months is required.')
          .typeError('Only numbers are allowed.')
      }),
      candidateExpectation: Yup.number().when('result', {
        is: HR_OVERALL_FEEDBACK_STATUS['Not Selected'],
        then: Yup.number('candidate expectation must be of type number.')
          .min(0, 'candidate expectation should not be less then 0.')
          .max(99, 'candidate expectation should not be more then 99.')
          .typeError('Only positive numbers are allowed.'),
        otherwise: Yup.number('candidate expectation must be of type number.')
          .min(0, 'candidate expectation should not be less then 0.')
          .max(99, 'candidate expectation should not be more then 99.')
          .typeError('Only positive numbers are allowed.')
          .required('Please enter candidate expectation')
      }),
      interviewerID:
        currentRound?.Status === STATUS_INTERVIEW.HR_PENDING ||
        currentRound?.Status === STATUS_INTERVIEW.HR_SCHEDULE ||
        currentRound?.Status === STATUS_INTERVIEW.HR_RESCHEDULE
          ? (() => {
              let schema = Yup.array()
                .min(1, 'Interviewer is required.')
                .required('Interviewer is required.');
              if (currentRound?.InterviewerLimit) {
                schema = schema.max(
                  currentRound.InterviewerLimit,
                  `Max ${currentRound.InterviewerLimit} interviewers can be selected.`
                );
              }
              return schema;
            })()
          : Yup.array()
    });
  }

  const submitHRFeedbackAfterConfirmation = (values) => {
    const interviewRoundId = userDetails?.ID;
    const formData = {
      interviewRoundId: id,
      // overallFeedback: values.overallFeedback.toString(),
      result: values.result.toString(),
      feedbackNote: values.feedbackNote,
      overallGrades: values.overallGrade.toString(),
      technicalAttitude: values.technicalAttitude ? values.technicalAttitude.toString() : '',
      technicalCommunication: values.technicalCommunication
        ? values.technicalCommunication.toString()
        : '',
      designation: values.jobDesignation,
      packagePerAnum: String(values.package ?? ''),
      yearsOfExperience: String(values.yearsOfExperience ?? ''),
      monthsOfExperience: String(values.monthsOfExperience ?? ''),
      candidateExpectations: values.candidateExpectation.toString(),
      candidateOfferedSalary: values.candidateOfferedSalary
        ? String(values.candidateOfferedSalary)
        : '',
      interviewType,
      ...(!!values.interviewerID?.length && { invitationIds: values.interviewerID.join(',') })
    };
    dispatch(apiStatusClear());
    dispatch(addFeedback(formData, interviewRoundId));
    props.onHide(false);
    setTimeout(() => {
      if (props.ByInterviewer) {
        dispatch(
          getInterviewHistoryList({
            searchValue: '',
            pageNumber: INITIAL_PAGE_NUMBER,
            size: INITIAL_SIZE,
            sortBy: 'DateModified',
            orderBy: 'desc'
          })
        );
        navigate(ROUTES.INTERVIEWER.INTERVIEW_HISTORY);
      }
    }, [250]);
  };

  const handleConfirmFeedbackFromHR = (values) => {
    confirmDialog({
      header: 'Confirmation',
      message:
        confirmDialogMessage || 'Are you sure you want to submit the feedback for this candidate?',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        submitHRFeedbackAfterConfirmation(values);
      },
      reject: () => {}
    });
  };

  const formik = useFormik({
    initialValues: {
      // overallFeedback: feedbackData?.overallFeedback || '',
      result: isHRRound ? currentRound?.Ratings?.Result : feedbackData?.result || '',
      overallGrade: isHRRound
        ? currentRound?.Ratings?.OverAllGrades
        : feedbackData?.overallGrade || '',
      technicalAttitude: isHRRound
        ? currentRound?.Ratings?.TechnicalAttitude || userDetails?.TechnicalAttitude || ''
        : feedbackData?.technicalAttitude || '',
      technicalCommunication: isHRRound
        ? currentRound?.Ratings?.TechnicalCommunication || userDetails?.TechnicalCommunication || ''
        : feedbackData?.technicalCommunication || '',
      codeQuality: isHRRound ? currentRound?.Ratings?.CodeQuality : feedbackData?.codeQuality || '',
      taskCompletion: isHRRound
        ? currentRound?.Ratings?.TaskCompletion
        : feedbackData?.taskCompletion || 0,
      feedbackNote: isHRRound
        ? currentRound?.InterviewRoundFeedback
        : feedbackData?.feedbackNote || '',
      interviewRoundId: '',
      jobDesignation: isHRRound
        ? currentRound?.Ratings?.Designation
        : feedbackData?.jobDesignation || '',
      package: isHRRound
        ? currentRound?.Ratings?.Package ?? userDetails?.Candidate?.CurrentCTC ?? ''
        : userDetails?.Candidate?.CurrentCTC ?? '',
      yearsOfExperience: isHRRound
        ? currentRound?.Ratings?.YearsOfExperience ??
          userDetails?.Candidate?.YearsOfExperience ??
          ''
        : userDetails?.Candidate?.YearsOfExperience ?? '',

      monthsOfExperience: isHRRound
        ? currentRound?.Ratings?.MonthsOfExperience ??
          userDetails?.Candidate?.MonthsOfExperience ??
          0
        : userDetails?.Candidate?.MonthsOfExperience ?? 0,
      candidateExpectation: isHRRound
        ? currentRound?.Ratings?.CandidateExpectations
        : feedbackData?.candidateExpectation || '',
      candidateOfferedSalary: isHRRound
        ? currentRound?.Ratings?.CandidateOfferedSalary
        : feedbackData?.candidateOfferedSalary || '',
      interviewerID:
        currentRound?.InterviewRoundInvitations?.filter(
          (invite) => invite.Status === STATUS.ACCEPT
        ).map((invite) => invite.ID) || []
    },
    validationSchema: validationShape,
    onSubmit: (values) => {
      if (interviewType === InterviewType['HR Round']) {
        handleConfirmFeedbackFromHR(values);
      }
    },
    enableReinitialize: true
  });

  const isFormFieldValid = (name) => !!(formik.touched[name] && formik.errors[name]);
  const getFormErrorMessage = (name, additionalError = '') => {
    let response = null;
    if (isFormFieldValid(name) || additionalError) {
      response = <small className="p-error">{formik.errors[name] || additionalError}</small>;
    }
    return response;
  };

  // const handleDesignationSearch = () => {
  //   if (designationText) {
  //     const sanitizeString = designationText.trim().toString();
  //     dispatch(getJobDesignationBySearch(sanitizeString));
  //   }
  // };
  const hrFeedbackOptions = useMemo(() => {
    const currentRes = String(currentRound?.Ratings?.Result || formik.values.result || '');
    const existsInLimited = LIMITED_HR_FEEDBACK.some((item) => item.code === currentRes);
    if (currentRes && !existsInLimited) {
      const legacyItem = HR_OVERALL_FEEDBACK.find((item) => item.code === currentRes);
      if (legacyItem) {
        return [...LIMITED_HR_FEEDBACK, legacyItem];
      }
    }
    return LIMITED_HR_FEEDBACK;
  }, [currentRound, formik.values.result]);

  return (
    <div className="form-box-wrapper feedback-form-popup">
      <form onSubmit={formik.handleSubmit}>
        <div className="form-row-wrapper">
          {interviewType === InterviewType['HR Round'] && (
            <>
              <div className="form-col">
                <div className="form-group-outer">
                  <div className="custom-form-group">
                    <Label htmlFor="result" text="Result" isMandatory />
                    <Dropdown
                      name="result"
                      id="result"
                      optionLabel="name"
                      optionValue="code"
                      value={formik.values.result}
                      options={hrFeedbackOptions}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      disabled={disabled}
                      placeholder="Select result"
                      className={classNames({
                        'p-invalid': isFormFieldValid('result'),
                        'w-full': true
                      })}
                    />
                    {getFormErrorMessage('result')}
                  </div>
                </div>
              </div>
              <div className="form-col">
                <div className="form-group-outer">
                  <div className="custom-form-group">
                    <Label
                      htmlFor="jobDesignation"
                      text={LABEL_TYPES.DESIGNATION}
                      isMandatory={
                        formik.values.result !== HR_OVERALL_FEEDBACK_STATUS['Not Selected']
                      }
                    />
                    {/* <AutoComplete
                      field="Designation"
                      name="jobDesignation"
                      id="jobDesignation"
                      forceSelection
                      value={formik.values.jobDesignation}
                      suggestions={designationOptions}
                      completeMethod={handleDesignationSearch}
                      onChange={(e) => {
                        if (typeof e.target.value === 'object') {
                          setDesignationText(e.target.value?.Designation);
                          formik.setFieldValue('jobDesignation', e.target.value?.Designation);
                        } else {
                          setDesignationText(e.target.value);
                          formik.setFieldValue('jobDesignation', e.target.value);
                        }
                      }}
                      disabled={disabled}
                      onBlur={formik.handleBlur}
                      placeholder="Select Designation"
                      className={classNames({
                        'p-invalid': isFormFieldValid('jobDesignation'),
                        'w-full': true
                      })}
                    /> */}
                    <Dropdown
                      name="jobDesignation"
                      id="jobDesignation"
                      value={formik.values.jobDesignation}
                      options={designationOptions || []}
                      optionLabel="Designation"
                      optionValue="Designation"
                      onChange={(e) => {
                        formik.setFieldValue('jobDesignation', e.value, true);
                        // formik.setFieldTouched('jobDesignation', true, true);
                      }}
                      disabled={disabled}
                      filter
                      filterPlaceholder="Search Designation"
                      placeholder="Select Designation"
                      className={classNames({
                        'p-invalid': formik.touched.jobDesignation && formik.errors.jobDesignation,
                        'w-full': true
                      })}
                    />
                    {(formik.touched.jobDesignation || formik.submitCount > 0) &&
                      formik.errors.jobDesignation && (
                        <small className="p-error">{formik.errors.jobDesignation}</small>
                      )}
                  </div>
                </div>
              </div>
              <div className="form-col">
                <div className="form-row-wrapper">
                  <div className="form-col">
                    <div className="custom-form-group mr-4">
                      <Label
                        htmlFor="yearsOfExperience"
                        text={LABEL_TYPES.YEAR_EXPERIENCE}
                        isMandatory={
                          formik.values.result !== HR_OVERALL_FEEDBACK_STATUS['Not Selected']
                        }
                      />
                      <InputNumber
                        id="yearsOfExperience"
                        name="yearsOfExperience"
                        placeholder="Years"
                        value={formik.values.yearsOfExperience}
                        onValueChange={(e) => formik.setFieldValue('yearsOfExperience', e.value)}
                        disabled={disabled}
                        className={classNames({
                          'p-invalid': isFormFieldValid('yearsOfExperience')
                        })}
                        min={0}
                        max={100}
                      />
                      {getFormErrorMessage('yearsOfExperience')}
                    </div>
                  </div>
                  <div className="form-col">
                    <div className="custom-form-group">
                      <Label
                        htmlFor="monthsOfExperience"
                        text={LABEL_TYPES.MONTH_EXPERIENCE}
                        isMandatory={
                          formik.values.result !== HR_OVERALL_FEEDBACK_STATUS['Not Selected']
                        }
                      />
                      <InputNumber
                        id="monthsOfExperience"
                        name="monthsOfExperience"
                        placeholder="0-11"
                        value={formik.values.monthsOfExperience}
                        onValueChange={(e) => formik.setFieldValue('monthsOfExperience', e.value)}
                        disabled={disabled}
                        className={classNames({
                          'p-invalid': isFormFieldValid('monthsOfExperience')
                        })}
                        min={0}
                        max={11}
                      />
                      {getFormErrorMessage('monthsOfExperience')}
                    </div>
                  </div>
                </div>
              </div>

              <div className="form-col">
                <div className="form-group-outer">
                  <div className="custom-form-group">
                    <Label
                      htmlFor="package"
                      text={LABEL_TYPES.CURRENT_PACKAGE}
                      isMandatory={
                        formik.values.result !== HR_OVERALL_FEEDBACK_STATUS['Not Selected']
                      }
                    />
                    <div className="p-inputgroup w-full">
                      <InputText
                        id="package"
                        name="package"
                        value={formik.values.package}
                        onChange={formik.handleChange}
                        className={classNames({
                          'p-invalid': isFormFieldValid('package'),
                          'w-full': true
                        })}
                        onBlur={formik.handleBlur}
                        disabled={disabled}
                        keyfilter={'money'}
                      />
                      <span className="p-inputgroup-addon add-candidate-lpa">LPA</span>
                    </div>
                    {getFormErrorMessage('package')}
                  </div>
                </div>
              </div>

              <div className="form-col">
                <div className="form-group-outer">
                  <div className="custom-form-group">
                    <Label
                      htmlFor="candidateExpectation"
                      text={LABEL_TYPES.CANDIDATE_EXPECTATION}
                      isMandatory={
                        formik.values.result !== HR_OVERALL_FEEDBACK_STATUS['Not Selected']
                      }
                    />
                    <div className="p-inputgroup w-full">
                      <InputText
                        id="candidateExpectation"
                        name="candidateExpectation"
                        value={formik.values.candidateExpectation}
                        onChange={formik.handleChange}
                        className={classNames({
                          'p-invalid': isFormFieldValid('candidateExpectation'),
                          'w-full': true
                        })}
                        onBlur={formik.handleBlur}
                        disabled={disabled}
                        keyfilter={'money'}
                      />
                      <span className="p-inputgroup-addon add-candidate-lpa">LPA</span>
                    </div>
                    {getFormErrorMessage('candidateExpectation')}
                  </div>
                </div>
              </div>
            </>
          )}

          <div className="form-col">
            <div className="form-group-outer">
              <div className="custom-form-group">
                <Label htmlFor="address" text="Select an overall grade" isMandatory={true} />
                <Dropdown
                  name="overallGrade"
                  id="overallGrade"
                  optionLabel="name"
                  optionValue="code"
                  value={formik.values.overallGrade}
                  options={OVERALL_GRADE}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  disabled={disabled}
                  placeholder="Select an overall grade"
                  className={classNames({
                    'p-invalid': isFormFieldValid('overallGrade'),
                    'w-full': true
                  })}
                />
                {getFormErrorMessage('overallGrade')}
              </div>
            </div>
          </div>
          <div className="form-col">
            <div className="form-group-outer">
              <div className="custom-form-group">
                <Label
                  htmlFor="technicalAttitude"
                  text="Select an attitude grade"
                  isMandatory={true}
                />
                <Dropdown
                  name="technicalAttitude"
                  id="technicalAttitude"
                  optionLabel="name"
                  optionValue="code"
                  value={formik.values.technicalAttitude}
                  options={OVERALL_GRADE}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  disabled={disabled}
                  placeholder="Select an attitude"
                  className={classNames({
                    'p-invalid': isFormFieldValid('technicalAttitude'),
                    'w-full': true
                  })}
                />
                {getFormErrorMessage('technicalAttitude')}
              </div>
            </div>
          </div>
          <div className="form-col">
            <div className="form-group-outer">
              <div className="custom-form-group">
                <Label
                  htmlFor="technicalCommunication"
                  text="Select communication grade"
                  isMandatory={true}
                />
                <Dropdown
                  name="technicalCommunication"
                  id="technicalCommunication"
                  optionLabel="name"
                  optionValue="code"
                  value={formik.values.technicalCommunication}
                  options={OVERALL_GRADE}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  disabled={disabled}
                  placeholder="Select communication"
                  className={classNames({
                    'p-invalid': isFormFieldValid('technicalCommunication'),
                    'w-full': true
                  })}
                />
                {getFormErrorMessage('technicalCommunication')}
              </div>
            </div>
          </div>
          <div className="form-col full-width">
            <div className="form-group-outer">
              <div className="custom-form-group">
                <Label
                  htmlFor="candidateOfferedSalary"
                  text={LABEL_TYPES.CANDIDATE_OFFERED_SALARY}
                  // isMandatory={formik.values.result !== HR_OVERALL_FEEDBACK_STATUS['Not Selected']}
                />
                <div className="p-inputgroup w-full">
                  <InputText
                    id="candidateOfferedSalary"
                    name="candidateOfferedSalary"
                    value={formik.values.candidateOfferedSalary}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled={disabled}
                    keyfilter="money"
                    className={classNames({
                      'p-invalid': isFormFieldValid('candidateOfferedSalary'),
                      'w-full': true
                    })}
                  />
                  <span className="p-inputgroup-addon add-candidate-lpa">LPA</span>
                </div>
                {/* {getFormErrorMessage('candidateOfferedSalary')} */}
              </div>
            </div>
          </div>

          {(currentRound?.Status === STATUS_INTERVIEW.HR_PENDING ||
            currentRound?.Status === STATUS_INTERVIEW.HR_SCHEDULE ||
            currentRound?.Status === STATUS_INTERVIEW.HR_RESCHEDULE) && (
            <div className="form-col full-width">
              <div className="form-group-outer">
                <div className="custom-form-group">
                  <Label htmlFor="interviewerID" text={LABEL_TYPES.INTERVIEWERS} isMandatory />
                  <MultiSelect
                    filter
                    name="interviewerID"
                    id="interviewerID"
                    optionLabel="name"
                    optionValue="code"
                    value={formik.values.interviewerID}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    options={currentRound?.InterviewRoundInvitations?.filter(
                      (invite) =>
                        invite.Status === STATUS.PENDING || invite.Status === STATUS.ACCEPT
                    ).map((invite) => ({
                      name: `${wordCapitalize(invite.Invited.FirstName)} ${wordCapitalize(
                        invite.Invited.LastName
                      )}`,
                      code: invite.ID
                    }))}
                    placeholder="Select"
                    display="chip"
                    className={classNames({
                      'p-invalid': isFormFieldValid('interviewerID'),
                      'w-full': true
                    })}
                    disabled={disabled}
                  />
                </div>
                {getFormErrorMessage('interviewerID')}
              </div>
            </div>
          )}

          <div className="form-col full-width">
            <div className="form-group-outer">
              <div className="custom-form-group">
                <Label htmlFor="Note" text="Note" isMandatory />
                <TextAreaWithCounterLimit
                  placeholder="Add Note"
                  value={formik.values.feedbackNote}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  errorMessage={formik.touched['feedbackNote'] ? formik.errors['feedbackNote'] : ''}
                  className={classNames({
                    'p-invalid': isFormFieldValid('feedbackNote')
                  })}
                  autoResize
                  id="feedbackNote"
                  name="feedbackNote"
                  htmlFor="feedbackNote"
                  validationOnMinLimit
                  minLimit={
                    interviewCategory === Interview_Category['Non Technical']
                      ? 4
                      : TEXTAREA_LIMIT.MIN
                  }
                  maxLimit={FEEDBACK_TEXTAREA_LIMIT.MAX}
                  rows={3}
                  cols={30}
                  disabled={disabled}
                />
              </div>
            </div>
          </div>
        </div>
        <div className="p-dialog-footer block">
          <CustomButton type="submit" variant="contained" disabled={isLoading || disabled}>
            {BUTTON_TYPES.SUBMIT}
          </CustomButton>
        </div>
      </form>
    </div>
  );
};

const AddHrFeedback = (props) => {
  const {
    id,
    onHide,
    show,
    interviewType,
    confirmDialogMessage,
    ByInterviewer,
    disabled,
    feedbackData,
    interviewCategory
  } = props;
  const { isLoading } = useSelector((state) => state.apiStatus);

  return (
    <Dialog
      header="HR Round Feedback "
      className={classNames('card overflow-hidden resize-none feedback-dialog', {
        hidden: isLoading
      })}
      visible={show}
      onHide={() => onHide(false)}>
      <AddFeedback
        id={id}
        onHide={onHide}
        disabled={disabled}
        interviewType={interviewType}
        confirmDialogMessage={confirmDialogMessage}
        feedbackData={feedbackData}
        ByInterviewer={ByInterviewer}
        interviewCategory={interviewCategory}
      />
    </Dialog>
  );
};

export default AddHrFeedback;
